import {ListFilterSelectOptions} from '@aviary/store/common/common.types';
import {MxSelector} from '@aviary/store/store.types';

export type TableCardHeaderFilterDefinitions = TableCardHeaderFilterDefinition[];

export type TableCardHeaderFilterDefinition = SelectFilterDefinition | DateRangeFilterDefinition | NotImplementedFilterDefinition;

export type SelectFilterDefinition = SingleSelectFilterDefinition | MultiSelectFilterDefinition;

export type FilterDefinitionType = TableCardHeaderFilterDefinition['type'];

export type FilterOptionsSelector = MxSelector<ListFilterSelectOptions>;

export interface BaseFilterDefinition {
  /**
   * REQUIRED.  The name of the query parameter that will be added to the route URL when this filter is active.  Note that names should be specified
   * as `lowercase`, and multi-word names should be specified as `lower-kebab-case`, because that's the format we should use for a URL query parameter.  When
   * this filter is inactive, the filter button's label will use this same text, but converted to `UPPER TITLE CASE`.
   *
   * Example: `queryParamName: 'date-range'` &rarr; button label = `'DATE RANGE'`.
   */
  queryParamName: string;
}

export interface BaseSelectFilterDefinition extends BaseFilterDefinition {
  /**
   * REQUIRED.  For dropdown-menu filters, this contains one of:
   * <ul>
   *   <li>
   *     an array of menu button names.  Note that names should be specified as <code>UPPERCASE</code>, and multi-word names should be specified as
   *     <code>UPPER_SNAKE_CASE</code>, because we want a consistent format for URL query parameter values.  The menu item's button label will use this same
   *     text, but converted to <code>Title Case</code>.
   *
   *     Example: <code>filterValues: ['HELD', 'CHECKED_OUT']</code> &rarr; menu button labels <code>'Held'</code> and <code>'Checked Out'</code>
   *   </li>
   *
   *   <li>
   *     a selector of <code>ListFilterSelectOptions</code>.  Button labels will be taken as-is from the `displayName` properties.
   *   </li>
   * </ul>
   */
  filterValues: string[] | FilterOptionsSelector;
}

export interface NotImplementedFilterDefinition extends BaseFilterDefinition {
  /** This type is not at all useful for production, but it provides a way to display a filter button even if we're not ready to implement it. */
  type: 'notImplemented';
}

export interface SingleSelectFilterDefinition extends BaseSelectFilterDefinition {
  type: 'singleSelect';
}

export interface MultiSelectFilterDefinition extends BaseSelectFilterDefinition {
  type: 'multiSelect';
}

export interface DateRangeFilterDefinition extends BaseFilterDefinition {
  type: 'dateRange';
}

export function isSelectFilterDefinition(definition: TableCardHeaderFilterDefinition): definition is SelectFilterDefinition {
  return 'filterValues' in definition;
}
